import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Wallet, Plus, Minus, Search, Mic2, Award, LogIn, TrendingUp, Handshake, Link } from 'lucide-react';
import img from "./logo.png"

// Sayfa, yerel dosyalara bağımlı kalmamak için logo yerine placeholder kullanır.
// Projenizde kendi logonuzun yolu ile değiştirebilirsiniz.
const logoUrl = img;

// Kredi işlemi kartları için bileşen
const CreditActionCard = ({ icon, title, description, creditAmount, type }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  
  const isGain = type === 'gain';
  const colorClass = isGain ? 'text-teal-400' : 'text-rose-400';
  const bgColorClass = isGain ? 'bg-teal-500/20' : 'bg-rose-500/20';

  return (
    <motion.div
      ref={ref}
      className="p-6 md:p-8 rounded-3xl shadow-xl border border-gray-700/50 backdrop-blur-md text-center flex flex-col items-center transform transition-all duration-300"
      // Glassmorphism için background gradient'i
      style={{ background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.5) 0%, rgba(17, 24, 39, 0.4) 100%)' }}
      // Kartın sayfaya girişi için animasyon
      initial={{ opacity: 0, scale: 0.8, y: 50 }}
      animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: "easeOut" }}
      // Hover animasyonu
      whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(99,102,241,0.5)" }}
    >
      <div className={`mb-4 p-4 rounded-full inline-flex items-center justify-center shadow-lg ${bgColorClass}`}>
        {icon}
      </div>
      <h3 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-300 mb-2">{title}</h3>
      <p className="text-gray-300 text-base md:text-lg mb-4 flex-grow">{description}</p>
      <div className={`mt-auto text-3xl font-extrabold ${colorClass} flex items-center gap-1`}>
        {isGain ? <Plus size={24} /> : <Minus size={24} />}
        {creditAmount}
        <span className="text-base font-semibold text-gray-400 ml-2">Kredi</span>
      </div>
    </motion.div>
  );
};

// Ana kredi sistemi sayfası bileşeni
export default function CreditSystemPage() {
  const gainActions = [
    {
      icon: <LogIn size={36} className="text-teal-300" />,
      title: "Hesap Oluştur",
      description: "Mekânname'ye katıldığınızda hesabınıza hoş geldin kredisi olarak anında yüklenir.",
      creditAmount: 1000,
      type: 'gain'
    },
    {
      icon: <Award size={36} className="text-teal-300" />,
      title: "Quiz Başarısı",
      description: "Mekânname Quizlerinde başarılı olduğunuzda bilginizin karşılığını alın.",
      creditAmount: 100,
      type: 'gain'
    },
    {
      icon: <TrendingUp size={36} className="text-teal-300" />,
      title: "Günlük Giriş Bonusu",
      description: "Her gün Mekânname'ye giriş yaparak keşiflerinize devam edin ve bonus kazanın.",
      creditAmount: 25,
      type: 'gain'
    },
  ];

  const spendActions = [
    {
      icon: <Search size={36} className="text-rose-300" />,
      title: "Mekan Tarama",
      description: "Bir fotoğraf yükleyerek tarihi mekanı taratın ve detaylı bilgiye ulaşın.",
      creditAmount: 75,
      type: 'spend'
    },
    {
      icon: <Mic2 size={36} className="text-rose-300" />,
      title: "Sesli Anlatım",
      description: "Bir mekanla ilgili yapay zeka destekli sesli anlatım oluşturun.",
      creditAmount: 50,
      type: 'spend'
    },
    {
      icon: <Handshake size={36} className="text-rose-300" />,
      title: "Uzman Görüşü",
      description: "Tarih uzmanlarından özel danışmanlık veya soru-cevap hizmeti alın.",
      creditAmount: 150,
      type: 'spend'
    },
  ];

  return (
    <div className="font-sans text-white min-h-screen antialiased overflow-x-hidden relative">
      {/* Arka plan için yumuşak radyal gradient ve bulutsu efekti */}
      <div className="absolute inset-0 bg-gray-950 -z-10">
        <div className="w-96 h-96 bg-purple-500/20 rounded-full blur-[150px] absolute top-10 left-10 animate-pulse"></div>
        <div className="w-80 h-80 bg-teal-500/20 rounded-full blur-[150px] absolute bottom-20 right-20 animate-pulse delay-500"></div>
      </div>

      <div className="p-6 md:p-12 relative z-10">
        {/* Sayfa başlığı ve tanıtım bölümü */}
        <motion.div
          className="max-w-4xl mx-auto text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src={logoUrl} alt="Mekânname Logo" className="w-16 h-16 rounded-full border-2 border-indigo-500 shadow-lg" />
            <h1 className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-teal-400 to-indigo-400 bg-clip-text text-transparent">
              Mekânname Kredi Sistemi
            </h1>
          </div>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Keşiflerinizi ve öğrenme maceranızı Mekânname Kredileri ile güçlendirin. Her etkileşiminiz artık daha değerli!
          </p>
        </motion.div>

        {/* Kredi Kazanma Bölümü */}
        <section className="max-w-7xl mx-auto mb-16">
          <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-12 text-teal-400">
            Kredi Kazanma Yolları
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {gainActions.map((action, index) => (
              <CreditActionCard
                key={index}
                icon={action.icon}
                title={action.title}
                description={action.description}
                creditAmount={action.creditAmount}
                type={action.type}
              />
            ))}
          </div>
        </section>
        
        {/* Kredi Harcama Bölümü */}
        <section className="max-w-7xl mx-auto mb-16">
          <h2 className="text-center text-3xl md:text-4xl font-extrabold mb-12 text-rose-400">
            Kredi Harcama Yolları
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {spendActions.map((action, index) => (
              <CreditActionCard
                key={index}
                icon={action.icon}
                title={action.title}
                description={action.description}
                creditAmount={action.creditAmount}
                type={action.type}
              />
            ))}
          </div>
        </section>
        
        {/* CTA (Harekete Geçirme) bölümü */}
        <section className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-teal-400 mb-4">
            Keşfetmeye Başla ve Kredi Kazan!
          </h3>
          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-xl mx-auto">
            Hemen bir hesap oluştur ve 1000 kredinle Mekânname'nin büyülü dünyasına adım at.
          </p>
          <a href="/signup">
            <motion.button 
              whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(99,102,241,0.7)" }}
              whileTap={{ scale: 0.95 }}
              className="bg-indigo-600 px-10 py-4 rounded-full font-bold text-white shadow-lg hover:bg-indigo-700 transition-colors text-lg"
            >
              Hemen Kaydol <Link size={20} className="inline-block ml-2 transform -rotate-45" />
            </motion.button>
          </a>
        </section>
      </div>
    </div>
  );
}
